import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { SwitchedLoanConditionsDto } from './SwitchedLoanConditionsDto'

// Query Keys
export const swapApplicantsKeys = {
  all: ['swapApplicantsSimulation'] as const,
  simulation: (financingProgramId: string, creditApplicationId: string) =>
    [...swapApplicantsKeys.all, financingProgramId, creditApplicationId] as const,
}

async function fetchSwapApplicantsSimulation(financingProgramId: string, creditApplicationId: string) {
  const apiClient = getApiClient()
  const url = `/Applications/${financingProgramId}/${creditApplicationId}/SwapApplicantsSimulation`
  const response = await apiClient.get<SwitchedLoanConditionsDto>(url)
  return response.data
}

async function applySwapApplicantsSimulation(financingProgramId: string, creditApplicationId: string) {
  const apiClient = getApiClient()
  const url = `/Applications/${financingProgramId}/${creditApplicationId}/SwapApplicants`
  await apiClient.post(url) 
}

export function useSwapApplicantsSimulation(
  financingProgramId: string,
  creditApplicationId: string,
  enabled = true,
) {
  return useQuery<SwitchedLoanConditionsDto, AxiosError>({
    queryKey: swapApplicantsKeys.simulation(financingProgramId, creditApplicationId),
    queryFn: () => fetchSwapApplicantsSimulation(financingProgramId, creditApplicationId),
    enabled,
    staleTime: 0,
  })
}

export function useApplySwapApplicants(): [
  MutationFunction<void, { financingProgramId: string; creditApplicationId: string }>,
  boolean,
] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ financingProgramId, creditApplicationId }: { financingProgramId: string; creditApplicationId: string }) =>
      applySwapApplicantsSimulation(financingProgramId, creditApplicationId),
    onSuccess: (_, { financingProgramId, creditApplicationId }) => {
      // Remove the simulation data since the swap has been applied
      queryClient.removeQueries({
        queryKey: swapApplicantsKeys.simulation(financingProgramId, creditApplicationId),
      })
    },
  })
  return [mutateAsync, isPending]
}

export default {
  fetchSwapApplicantsSimulation,
  applySwapApplicantsSimulation,
}